import * as React from 'react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import * as Yup from 'yup';
import { useFormik } from 'formik';

import {
  PopupNames,
  useVerifyTeacherLazyQuery,
  VerifyTeacherQuery,
} from 'core';
import {
  usePopupStateOperations,
  useSortErrors,
  useCreateTeacher,
  usePopupData,
} from 'core/hooks';

import { yupValidation } from 'services/YupValidationService';
import { translateRoleToValue } from 'services/TransformService';
import { PATHS } from 'router/config';

import { ErrorsType, ServerCodes, UserRole } from 'types';

import { ISelectOption } from 'components/atoms';
import { TeacherQuickAddPopupComponent } from './TeacherQuickAddPopup';
import { ConfirmPopupComponent } from '../ConfirmPopup/ConfirmPopup';
import { ITeacherAddFieldsValues } from '../TeacherAddPopup/TeacherAddFields';

export const getTeacherQuickAddSchema = (t: TFunction) =>
  Yup.object().shape({
    firstName: yupValidation.firstName(
      `${t('teacherPopup.fields.firstName.labelSecond')}`,
      t,
    ),
    middleName: yupValidation.middleName(
      `${t('teacherPopup.fields.middleName.labelSecond')}`,
      t,
    ),
    lastName: yupValidation.lastName(
      `${t('teacherPopup.fields.lastName.labelSecond')}`,
      t,
    ),
    phone: yupValidation.phone(`${t('teacherPopup.fields.phone.label')}`, t),
    email: yupValidation.email(`${t('teacherPopup.fields.email.label')}`, t),
  });

const buildTeacherInput = (v: ITeacherAddFieldsValues) => ({
  firstName: v.firstName || '',
  lastName: v.lastName || '',
  middleName: v.middleName || undefined,
  email: v.email || '',
  phone: v.phone || '',
  siteDisplay: v.displayOnSite || false,
});

const fullNameOf = (u?: {
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
}) => [u?.lastName, u?.firstName, u?.middleName].filter(Boolean).join(' ');

interface IQuickConfirm {
  title: string;
  body: string;
  confirmText: string;
  onConfirm: () => void;
}

/**
 * Standalone («свободный») teacher quick-add (spec 0007 rev.). On submit it
 * first calls `verifyTeacher` to see whether this person already exists — one
 * human can be both a student and a teacher:
 *   - new person        → create straight away (`createTeacher`);
 *   - exists (student…) → confirm, then ADD the teacher role (backend appends
 *     the role, keeping the existing ones);
 *   - already a teacher → offer to open their existing card (no second Teacher).
 * Mirrors the verify→confirm UX of the full add-with-application flow, but skips
 * the employment-type / application steps.
 *
 * The confirmation is rendered LOCALLY (not via the global `confirmPopup`) so
 * this container — and its `useCreateTeacher` hook — stays mounted: switching to
 * a different global popup would unmount it, and the mutation's success/error
 * effects (toast + navigate) would silently never fire.
 */
export const TeacherQuickAddPopup = memo(() => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const savedValues = usePopupData(PopupNames.teacherQuickAddPopup);
  const [disabledButton, setDisabledButton] = useState(true);
  const [confirm, setConfirm] = useState<IQuickConfirm | null>(null);

  const {
    operations: { hidePopup },
  } = usePopupStateOperations();

  // Latest submitted form values — read fresh (via ref) inside the verify
  // onCompleted handler to build the create input, avoiding a stale closure.
  const submittedRef = useRef<ITeacherAddFieldsValues>({});

  const onClose = useCallback(() => {
    hidePopup(PopupNames.teacherQuickAddPopup);
  }, [hidePopup]);

  const onCreated = useCallback(
    (teacherId: string) => {
      hidePopup(PopupNames.teacherQuickAddPopup);
      history.push(PATHS.teacherDetail.replace(':id', teacherId));
    },
    [hidePopup, history],
  );

  const [
    createTeacher,
    { loading: createLoading, errors: serverErrors },
  ] = useCreateTeacher(onCreated);

  const handleVerified = useCallback(
    (data: VerifyTeacherQuery) => {
      const res = data?.verifyTeacher;
      if (!res) return;
      const input = buildTeacherInput(submittedRef.current);

      // Brand-new person → create straight away.
      if (res.code === ServerCodes.success) {
        createTeacher({ teacher: input }, false);
        return;
      }

      // credentials text (RU/PL), mirroring the full add-teacher flow.
      const phone = res.user?.phone || res.teacher?.user?.phone;
      const email = res.user?.email || res.teacher?.user?.email;
      const numberText = phone && `${t('teacherPopup.number')} ${phone}`;
      const emailText = email && `${t('teacherPopup.mail')} ${email}`;
      const joiner = i18n.language === 'ru' ? 'и' : 'i';
      const both =
        numberText && emailText && `${numberText} ${joiner} ${emailText}`;
      const prefix = i18n.language === 'ru' ? 'с' : 'z';
      const credentialsText = `${prefix} ${
        both || numberText || emailText || ''
      }`;

      // Exists as a student / other role → confirm, then add the teacher role.
      // The matched person's name is shown so an admin can catch a mistyped
      // email/phone that resolved to the wrong existing user.
      if (res.code === ServerCodes.userExist) {
        const roles = res.user?.roles
          ?.map((role) => translateRoleToValue(role as UserRole))
          .join(', ');
        const name = fullNameOf(res.user || undefined);
        setConfirm({
          title: `${t('teacherPopup.teacherIsInSystem')}`,
          body: `${name ? `${name} — ` : ''}${t(
            'teacherPopup.addAnotherAccess',
            { roles, credentialsText },
          )} "${translateRoleToValue(UserRole.TEACHER)}"?`,
          confirmText: `${t('teacherPopup.button.add')}`,
          onConfirm: () => {
            setConfirm(null);
            createTeacher({ teacher: input }, true);
          },
        });
        return;
      }

      // Already a teacher → can't create a second one; offer to open the card.
      if (
        res.code === ServerCodes.teacherExist ||
        res.code === ServerCodes.teacherAgreementActive
      ) {
        const teacherId = res.teacher?.id;
        setConfirm({
          title: `${t('teacherPopup.teacherIsInSystem')}`,
          body: `${t('teacherPopup.openTeacherCard', {
            teacherFullname: fullNameOf(res.teacher?.user || undefined),
          })}`,
          confirmText: `${t('teacherPopup.button.open')}`,
          onConfirm: () => {
            setConfirm(null);
            if (teacherId) {
              hidePopup(PopupNames.teacherQuickAddPopup);
              history.push(PATHS.teacherDetail.replace(':id', teacherId));
            }
          },
        });
      }
    },
    [createTeacher, hidePopup, history, t, i18n],
  );

  const [verifyTeacher, { loading: verifyLoading }] = useVerifyTeacherLazyQuery(
    {
      fetchPolicy: 'network-only',
      onCompleted: handleVerified,
    },
  );

  const onSubmitHandler = useCallback(
    (formValues: ITeacherAddFieldsValues) => {
      submittedRef.current = formValues;
      verifyTeacher({
        variables: {
          firstName: formValues.firstName || '',
          middleName: formValues.middleName,
          lastName: formValues.lastName || '',
          email: formValues.email || '',
          phone: formValues.phone || '',
        },
      });
    },
    [verifyTeacher],
  );

  const { values, errors, handleSubmit, setFieldValue } = useFormik({
    initialValues: savedValues || {},
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema: getTeacherQuickAddSchema(t),
    onSubmit: onSubmitHandler,
    enableReinitialize: true,
  });

  const handleChange = useCallback(
    (name: string, value?: string | boolean | ISelectOption<string>[]) => {
      setFieldValue(name, value);
    },
    [setFieldValue],
  );

  useEffect(() => {
    const filled = Boolean(
      values.phone && values.email && values.firstName && values.lastName,
    );
    setDisabledButton(!filled);
  }, [values]);

  const [
    sortErrors,
    { errors: sortedErrors, message },
  ] = useSortErrors<ITeacherAddFieldsValues>();

  useEffect(() => {
    const clientErrors = Object.values(errors).length;
    if (clientErrors) {
      sortErrors(errors as ErrorsType<ITeacherAddFieldsValues>);
      return;
    }

    if (serverErrors) {
      sortErrors(serverErrors);
      return;
    }

    sortErrors(null);
  }, [errors, serverErrors, sortErrors]);

  if (confirm) {
    return (
      <ConfirmPopupComponent
        title={confirm.title}
        confirmText={confirm.confirmText}
        onConfirm={confirm.onConfirm}
        onCancel={() => setConfirm(null)}
        loading={createLoading}
      >
        {confirm.body}
      </ConfirmPopupComponent>
    );
  }

  return (
    <TeacherQuickAddPopupComponent
      values={values}
      errors={sortedErrors}
      errorMessage={message}
      onInputChange={handleChange}
      handleSubmit={handleSubmit}
      onClose={onClose}
      loading={verifyLoading || createLoading}
      disabledButton={disabledButton}
    />
  );
});
